import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

export default function SuccessStories() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/success-stories`)
      .then((res) => setStories(res.data.stories || []))
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-[#F8FAFC]" data-testid="success-stories-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-[#F8FAFC]">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 50% 0%, #0D9488 0%, transparent 55%)' }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-600/20 border border-teal-500/30 mb-6">
              <Trophy className="w-3.5 h-3.5 text-teal-300" />
              <span className="text-xs font-medium text-teal-300 tracking-wide">Employee Retention Case Studies</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold text-white leading-tight mb-5">
              Success Stories: Real HR Analytics ROI
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-slate-400 leading-relaxed">
              See how forward-thinking organizations are reducing employee turnover and building healthier workplace cultures with Udvega — without ever compromising individual privacy.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Story cards */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : stories.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-500">Success stories coming soon. Check back shortly.</p>
            </div>
          ) : (
            <div className="space-y-12">
              {stories.map((story, idx) => (
                <motion.article
                  key={story.slug}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  data-testid={`success-story-${story.slug}`}
                  className="bg-white rounded-3xl overflow-hidden shadow-xl shadow-slate-300/30 hover:shadow-2xl hover:shadow-teal-500/10 transition-all duration-300 group"
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
                    {/* Image */}
                    <div className={`relative h-64 lg:h-auto min-h-[280px] overflow-hidden ${idx % 2 === 1 ? 'lg:[direction:ltr]' : ''}`}>
                      {story.summary_image ? (
                        <img
                          src={story.summary_image}
                          alt={story.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-teal-600 to-slate-800" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-teal-700">
                          {story.industry}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`p-8 lg:p-10 flex flex-col justify-between ${idx % 2 === 1 ? 'lg:[direction:ltr]' : ''}`}>
                      <div>
                        <p className="text-sm font-semibold text-teal-600 mb-3">{story.one_line_achievement}</p>
                        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-800 mb-4 leading-snug">
                          {story.title}
                        </h2>
                        <p className="text-sm text-slate-500 leading-relaxed mb-6">{story.brief_intro}</p>

                        {story.stats && (
                          <div className="grid grid-cols-3 gap-4 mb-6">
                            {story.stats.map((s) => (
                              <div key={s.label} className="text-center p-3 rounded-xl bg-slate-50">
                                <p className="font-heading text-xl font-semibold text-slate-800">{s.value}</p>
                                <p className="text-[11px] text-slate-400 mt-1 leading-tight">{s.label}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end">
                        <Link
                          to={`/success-stories/${story.slug}`}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-teal-600 hover:text-teal-700 group/btn transition-colors"
                          data-testid={`read-story-${story.slug}`}
                        >
                          <span className="relative">
                            View Impact Study
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-teal-600 group-hover/btn:w-full transition-all duration-300" />
                          </span>
                          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-3xl font-semibold text-slate-800 mb-4">Your Success Story Could Be Next</h2>
          <p className="text-base text-slate-500 mb-8 max-w-xl mx-auto">
            Join organizations reducing employee turnover with predictive HR analytics and 100% anonymous pulse surveys.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-10 shadow-lg shadow-teal-600/25" data-testid="success-stories-cta-btn">
              Book a Demo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
